import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  authResetState,
  changePasswordRequest,
} from "../../redux/auth/AuthAction";
import CustomButton from "../constant/commonButton";
import ReusableFormFields from "../constant/reusableFormFields";
import CommonDialog from "../notification/ErrorDialogbox";

const ChangePassword = () => {
  const {
    control,
    handleSubmit,
    register,
    reset,
    setError,
    formState: { errors },
    getValues,
    watch,
  } = useForm();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);

  const dispatch = useDispatch();
  const forgotState = useSelector((state) => state?.auth?.changePassword || {});

  const newPassword = watch("newPassword");

  useEffect(() => {
    if (newPassword && newPassword.length > 0) {
      setShowPasswordRequirements(true);
    } else {
      setShowPasswordRequirements(false);
    }
  }, [newPassword]);

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
      password
    );

    if (password.length < minLength) {
      return `Password must be at least ${minLength} characters long`;
    }
    if (!hasUpperCase) {
      return "Password must contain at least 1 uppercase letter";
    }
    if (!hasNumber) {
      return "Password must contain at least 1 number";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least 1 special character";
    }
    return true;
  };

  const inputFields = [
    {
      name: "original_password",
      label: "Original Password",
      rules: { required: "Original Password is required" },
      type: "password",
    },
    {
      name: "newPassword",
      label: "New Password",
      type: "password",
      rules: {
        required: "New Password is required",
        validate: validatePassword,
      },
    },
    {
  name: "confirmPassword",
  label: "Confirm Password",
  type: "password",
  rules: {
    required: "Confirm Password is required",
    validate: (value) => {
      const newPassword = getValues("newPassword");
      const originalPassword = getValues("original_password");

      if (value !== newPassword) {
        return "Passwords do not match";
      }
      if (newPassword === originalPassword) {
        return "New password cannot be the same as the original password";
      }
      return true;
    },
  },
}

  ];

  const onSubmit = async (data) => {
    const new_password = data?.confirmPassword;
    const current_password = data?.original_password;
    const formData = { current_password, new_password };
    dispatch(changePasswordRequest(formData));
  };

  useEffect(() => {
    if (forgotState?.success) {
      setDialogMessage("Password updated successfully");
      setIsSuccess(true);
      setDialogOpen(true);
      dispatch(authResetState());
      reset({
        original_password: "",
        newPassword: "",
        confirmPassword: "",
      });
    } else if (forgotState?.error) {
      const errorFields = forgotState?.errormessage || {};

      if (typeof errorFields === "string") {
        setError("original_password", {
          type: "manual",
          message: errorFields,
        });
      } else if (
        typeof errorFields === "object" &&
        Object.keys(errorFields).length > 0
      ) {
        Object.keys(errorFields).forEach((field) => {
          setError(field, {
            type: "manual",
            message: errorFields[field],
          });
        });
      } else {
        setDialogMessage("Error occurred");
        setIsSuccess(false);
        setDialogOpen(true);
      }
    }
  }, [forgotState]);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <Box padding={{ xs: 1.5, sm: 3, md: 3, lg: 1, xl: 3 }}>
          <Box paddingLeft={2}>
            <Typography
              className="headingTable"
              variant="h3"
              component="h2"
              fontWeight={700}
            >
              <h1> Update Password </h1>
            </Typography>

            <ReusableFormFields
              inputFields={inputFields}
              control={control}
              errors={errors}
              register={register}
            />
          </Box>

          {/* Conditionally render Password Requirements Display */}
          {showPasswordRequirements && (
            <Box
              sx={{ mb: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1, ml: 2 }}
            >
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Password Requirements:
              </Typography>
              <Typography
                variant="body2"
                component="ul"
                sx={{ margin: 0, paddingLeft: 2 }}
              >
                <li>Minimum 8 characters</li>
                <li>At least 1 uppercase letter</li>
                <li>At least 1 number</li>
                <li>At least 1 special character (!@#$%^&*)</li>
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              gap: "20px",
              paddingLeft: "29px",
            }}
          >
            <CustomButton
              text="Submit"
              type="submit"
              variant="contained"
              size="SubmitButton"
              buttonStyle="StyledButton"
              disabled={forgotState?.inProgress}
              loading={forgotState?.inProgress}
              onClick={handleSubmit(onSubmit)}
            >
              Submit
            </CustomButton>

            <CustomButton
              text="Cancel"
              type="button"
              variant="contained"
              size="SubmitButton"
              buttonStyle="style1"
              onClick={() => reset()}
            >
              Cancel
            </CustomButton>
          </Box>
        </Box>
      </Grid>
      <CommonDialog
        open={dialogOpen}
        isSuccess={isSuccess}
        onClose={handleDialogClose}
        messageSuccess={dialogMessage}
        messageError={dialogMessage}
      />
    </Grid>
  );
};

export default ChangePassword;
